import { Grid, makeStyles } from '@material-ui/core';
import { getCurrentSession } from '@nutrien/cxp-auth-module';
import { Button, Typography } from '@nutrien/cxp-components';
import { Auth } from 'aws-amplify';
import React, { useEffect, useRef, useState } from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import { withRouter } from 'react-router-dom';

import config from '../../config/appConfig';
import { sentryCaptureException } from '../../utilities/sentryCaptureException';
import { useNotification } from '../../utilities/useNotification';
import { LS_CURRENT_USER_KEY, KEEP_LOGGED_IN_KEY } from '../../utilities/constants';
import { LoginSteps } from '../../utilities/enums';
import ForcedPasswordPopup from './ForcedPasswordPopUp';
import LoginFlowPassword from './LoginFlowPassword';
import LoginFlowUsername from './LoginFlowUsername';

const useStyles = makeStyles({
  nutrienSigninButtonGrid: {
    width: '100%',
  },
  dividerLabel: {
    paddingBottom: 16,
  },
});
interface Props extends RouteComponentProps {
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
}

const LoginFlow: React.FC<Props> = ({ history, isLoading, setIsLoading, location }: Props) => {
  const classes = useStyles();
  const loginFlowPasswordRef = useRef<{ signInFormSubmit: () => void }>();
  const [loginStep, setLoginStep] = useState<LoginSteps>(LoginSteps.USERNAME);
  const [email, setEmail] = useState<string>('');
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);
  const [ssoLoggingIn, setssoLoggingIn] = useState<boolean>(false);
  const [forcedPopupOpen, setforcedPopupOpen] = useState<boolean>(false);
  const [userObj, setuserObj] = useState<object>();
  const { successNotification, errorNotification } = useNotification();
  const updateEmail = async (emailId: string) => {
    setEmail(emailId);
  };

  const setMaxLoginExpiryTime = async () => {
    const rememberMe = localStorage.getItem(KEEP_LOGGED_IN_KEY);

    if (rememberMe === null) {
      return;
    }
  };

  const goToStep = (step: LoginSteps) => {
    setLoginStep(step);
  };

  const postLogin = async () => {
    await setMaxLoginExpiryTime();
    history.push('/initializing');
    return '';
  };

  const attemptUserSignIn = async (password: string): Promise<string> => {
    setIsLoggingIn(true);

    try {
      const result = await Auth.signIn(email, password);
      if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setuserObj(result);
        setforcedPopupOpen(true);
        return '';
      }
      if (result.challengeName === 'FORCE_CHANGE_PASSWORD') {
        setLoginStep(LoginSteps.FORGOT_PASSWORD);
        return '';
      }
    } catch (err) {
      setIsLoggingIn(false);
      const error = err as { code: string; message: string; name: string };
      if ('code' in error) {
        switch (error.code) {
          case 'UserNotFoundException':
            return 'Account not found.';
          case 'NotAuthorizedException':
            if ('message' in error && error.message === 'User is disabled.') {
              return 'Account disabled.';
            }
            if (
              'message' in error &&
              error.message ===
                'Temporary password has expired and must be reset by an administrator.'
            ) {
              return 'Your temporary password has expired, please contact your Nutrien Representative for assistance.';
            }
            return 'The username or password is incorrect. Try again or contact IT.';
          case 'PasswordResetRequiredException':
            if ('message' in error && error.message === 'Password reset required for the user') {
              return 'Password Expired. Use the Forgot Password link below to reset it.';
            }
        }
      } else {
        return 'An unknown error occurred.';
      }
    }

    return (await postLogin()) || '';
  };

  const clearRememberMeFromLocalStorage = () => {
    localStorage.removeItem(KEEP_LOGGED_IN_KEY);
  };

  const checkForSession = async () => {
    if (location.search.includes('code')) {
      setssoLoggingIn(true);
    }
    try {
      await getCurrentSession();
      await postLogin();
    } catch (error) {
      const isLoggedIn = localStorage.getItem(LS_CURRENT_USER_KEY);
      if (isLoggedIn) {
        sentryCaptureException(error, 'Error fetching current session on login flow');
      }

      clearRememberMeFromLocalStorage();
    }
    setIsLoading(false);
  };

  useEffect(() => {
    checkForSession();
  }, [location.search]);

  const setForcedPassword = async (newPassword: string) => {
    try {
      await Auth.completeNewPassword(userObj, newPassword, []);
      successNotification('Password Successfully Saved');
    } catch (err) {
      errorNotification('Please contact administrator to change password.');
    }
    setforcedPopupOpen(false);
    setIsLoggingIn(false);
  };

  const signInWithOffice365 = () => {
    setssoLoggingIn(true);
    const userPoolWebClientId = config.aws_user_pools_web_client_id;
    const { domain, redirectSignIn, office365IdentityProvider } = config.Auth.oauth;
    window.location.replace(
      `https://${domain}/oauth2/authorize?identity_provider=${office365IdentityProvider}&redirect_uri=${redirectSignIn}&response_type=CODE&client_id=${userPoolWebClientId}&scope=email openid profile`,
    );
  };

  const handleOnSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loginFlowPasswordRef.current) {
      loginFlowPasswordRef.current.signInFormSubmit();
    }
  };

  const getLoginStep = () => {
    switch (loginStep) {
      case LoginSteps.USERNAME:
        return (
          <>
            <Grid item container xs={12} justifyContent="center">
              <Grid className={classes.nutrienSigninButtonGrid}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={signInWithOffice365}
                  isLoading={ssoLoggingIn}
                  data-cy="loginAsUser"
                >
                  <Typography variant="subtitle1" fontWeight={700}>
                    Sign in with Nutrien ID
                  </Typography>
                </Button>
              </Grid>
              <Typography className={classes.dividerLabel}>Or</Typography>
            </Grid>
            <form onSubmit={handleOnSubmit}>
              <LoginFlowUsername updateEmail={updateEmail} />
              <LoginFlowPassword
                ref={loginFlowPasswordRef}
                goToStep={goToStep}
                email={email}
                attemptUserSignIn={attemptUserSignIn}
                isLoggingIn={isLoggingIn}
              />
            </form>
            {forcedPopupOpen && (
              <ForcedPasswordPopup
                open={forcedPopupOpen}
                onCancel={() => {
                  setIsLoggingIn(false);
                  setforcedPopupOpen(false);
                }}
                onForcedPassword={setForcedPassword}
              />
            )}
          </>
        );
      case LoginSteps.FORGOT_PASSWORD:
        history.push('/forgot-password');
    }
  };

  return <>{!isLoading && getLoginStep()}</>;
};

export default withRouter(LoginFlow);
